<template>
  <HKLModal
    ref="modal"
    size="large"
    dialogStyleOpt="stickyTop"
    :closable="true"
    :destroyOnClose="true"
    :afterClose="afterCloseHandle"
    @open="openModalHandle"
  >
    <HKLForm
      v-if="!isTenant"
      ref="form"
      :canEdit="shouldShowSubject"
    >
      <HKLFormItem
        :label="$t('subject')"
        :required="isPICAndNotSent"
      >
        <HKLInput
          v-if="isPICAndNotSent"
          v-model="displaySubject"
          v-hkldinput
          class="hkl-input-with-95"
          v-fusioninputcheck="{
            type: 'string',
            required: true
          }"
        />
        {{ displaySubject }}
      </HKLFormItem>
      <EmailGroupEditor
        v-if="canEditRecipients"
        v-model="receivers"
      />
    </HKLForm>
    <CommentsPreviewBodyRecipients
      v-if="isHKlandUser && shouldShowReceivers"
      :receivers="receivers"
    />
    <CommentsPreviewBody
      :isBatchesView="false"
      :activeBatch="activeBatch"
      :showRecipientsReadOnly="true"
    />
    <TenantSubmissionDocuments
      :files.sync="files"
      :sourceId="previewDataId"
      :canEdit="false"
    />
    <div
      class="row align-right nav_btn"
    >
      <HKLButton
        v-if="canEditRecipients"
        v-hkldreset
        color="darkblue"
        action="save"
        @click="saveReceivers()"
      />
      <HKLButton
        v-if="canSend"
        v-hkldreset
        color="orange"
        :action="hasSend ? 'resend' : 'send'"
        @click="sendToReceivers()"
      />
    </div>
    <!-- @TODO Use SendHistory and ReviewerHistory components for PostFitOutNextReviewer.vue and PostFitOutCommentHistory.vue  -->
    <GreenFitOutSendHistory v-if="isHKlandUser" />
    <GreenFitOutReviewerHistory v-if="isHKlandUser" />
    <template slot="footer">
      <div />
    </template>
  </HKLModal>
</template>

<script>
import { emailSeparator } from '@/api/postman/consts'
import { sendEmail } from '@/api/postman/request'
import { getUserEmailsByPermission, processDuplicateReceivers } from '@/api/postman/util'
import { updateGreenFitoutSubmission } from '@/api/tenantProjects/greenFitout.request'
import HKLButton from '@/controls/Button/HKLButton'
import EmailGroupEditor from '@/controls/Email/EmailGroupEditor'
import HKLForm from '@/controls/Form/HKLForm'
import HKLFormItem from '@/controls/Form/HKLFormItem'
import { useRefForm } from '@/controls/Form/hook'
import HKLInput from '@/controls/Input/HKLInput'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { DEPENDENCY_KEYS } from '@/modules/tenantProjects/consts'
import { utils } from '@/plugin/InputChange'
import { useStoreGetters } from '@/store'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import lodash from 'lodash'
import { computed, getCurrentInstance, inject, reactive, ref, toRefs, unref, watch } from 'vue-demi'
import { useCurrentTenantProjectBasicInfo, useHistoryReceivers } from '../hook'
import { buildUpdatePostFitoutRequest } from '../postFitOutLetter/util'
import CommentsPreviewBody from '../submissions/CommentsPreviewBody'
import TenantSubmissionDocuments from '../submissions/TenantSubmissionDocuments'
import { concatUserEmails, getProjectName } from '../utils'
import GreenFitOutReviewerHistory from './GreenFitOutReviewerHistory'
import GreenFitOutSendHistory from './GreenFitOutSendHistory'
import { GREEN_FITOUT_HISTORY_ACTION_TYPE, GREEN_FITOUT_HISTORY_TYPE } from './consts'
import { useGreenFitoutPerms, useRefreshCurrentGreenFitout } from './hook'
import { buildGreenFitOutHistory } from './utils'
import CommentsPreviewBodyRecipients from '../submissions/CommentsPreviewBodyRecipients.vue'
/// <reference types="vue-demi" />
export default {
  components: {
    CommentsPreviewBodyRecipients,
    HKLFormItem,
    HKLInput,
    HKLForm,
    HKLModal,
    HKLButton,
    EmailGroupEditor,
    CommentsPreviewBody,
    TenantSubmissionDocuments,
    GreenFitOutReviewerHistory,
    GreenFitOutSendHistory,
  },
  props: {
    previewData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props) {
    const { currentUser, isTenant, isHKlandUser, currentUserEmail } = useStoreGetters('user', ['currentUser', 'isTenant', 'isHKlandUser', 'currentUserEmail'])
    const { currentTenantProject } = useStoreGetters('tenantProject', ['currentTenantProject'])
    const { proxy } = getCurrentInstance()
    const files = ref([])
    const modals = useRefModal()

    const previewDataHistories = computed(() => {
      const histories = lodash.get(unref(props.previewData), 'histories') || []
      return lodash.orderBy(histories, 'createdAt', 'desc')
    })
    const latestHistoryReceivers = computed(() => {
      const validateTypes = [
        GREEN_FITOUT_HISTORY_ACTION_TYPE.ADD_FORDER,
        GREEN_FITOUT_HISTORY_ACTION_TYPE.RECEIVERS,
        GREEN_FITOUT_HISTORY_ACTION_TYPE.RESEND,
        GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND,
      ]
      const matchedHistory = unref(previewDataHistories).find(h => validateTypes.includes(h.actionType))
      return matchedHistory ? matchedHistory.receivers : null
    })
    const previewDataId = computed(() => (lodash.get(unref(props.previewData), 'id') || ''))
    const previewDataOrder = computed(() => (lodash.get(unref(props.previewData), 'order')))

    const { canCommentOnGreenFitout, canSend, canProjectRead, canPreview } = useGreenFitoutPerms()
    const refresh = useRefreshCurrentGreenFitout()
    const activeBatch = computed(() => {
      return {
        techComment: {
          id: unref(previewDataId),
          description: lodash.get(unref(props.previewData), 'comments.description'),
        },
        order: unref(previewDataOrder),
        actionHistory: unref(previewDataHistories).map(item => ({
          ...item,
          emailReceivers: item.receivers,
        })),
      }
    })
    const hasSend = computed(() => {
      return unref(previewDataHistories).some(h => h.actionType === GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND)
    })
    const isPic = computed(() => unref(props.previewData || {}).managedBy === unref(currentUser).id)
    const isPICAndCanManage = computed(() => {
      return canCommentOnGreenFitout.value && unref(isPic)
    })
    const canEditRecipients = computed(() => {
      if (unref(isPic) && unref(canSend)) return true
      return hasSend.value ? unref(canCommentOnGreenFitout) : unref(isPICAndCanManage)
    })
    const ccEmailsByRight = ref([])
    const defaultReceivers = useHistoryReceivers(latestHistoryReceivers)
    const receivers = computed({
      get () {
        const defaultCc = unref(defaultReceivers).cc
        const defaultBcc = unref(defaultReceivers).bcc
        const hasCurrentUserEmail = defaultCc.includes(unref(currentUserEmail)) || defaultBcc.includes(unref(currentUserEmail))
        const cc = []
        if (canSend.value && !hasCurrentUserEmail) {
          cc.push(unref(currentUserEmail))
        }
        if (!setupData.hadChangedEmailMetadata) {
          cc.push(...unref(ccEmailsByRight))
        }
        const parsedReceivers = processDuplicateReceivers(
          Object.assign({}, unref(defaultReceivers),
            { cc: concatUserEmails([defaultCc, ...cc], emailSeparator) }),
        )

        return lodash.isEmpty(setupData.pendingReceivers) ? parsedReceivers : setupData.pendingReceivers
      },
      set (value) {
        setupData.pendingReceivers = value
      },
    })
    const { building, tenancyId, tenancyType } = useCurrentTenantProjectBasicInfo()
    watch(() => props.previewData, async (newVal, oldVal) => {
      if (!newVal) return
      const isChanged = lodash.has(unref(newVal), 'id') && unref(newVal).id !== lodash.get(unref(oldVal), 'id')
      if (isChanged && building.value.id && tenancyType) {
        const emails = await getUserEmailsByPermission({
          buildingId: unref(building).id,
          tenancyId: unref(tenancyId),
          tenancyType: unref(tenancyType),
          permTypes: PermTypeMapKeys.GREEN_FIT_OUT_COMPLETE_CC,
        })
        ccEmailsByRight.value = lodash.uniq(emails)
      }
    }, { immediate: true })

    const setupData = reactive({
      pendingReceivers: {},
      isTenant,
      isHKlandUser,
      hadChangedEmailMetadata: computed(() => {
        const expectedActionTypes = [GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND, GREEN_FITOUT_HISTORY_ACTION_TYPE.RECEIVERS]
        return unref(previewDataHistories).some(h => expectedActionTypes.includes(h.actionType))
      }),
      pendingSubject: '',
      historySubject: computed(() => {
        const targetActionType = hasSend.value ? GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND : GREEN_FITOUT_HISTORY_ACTION_TYPE.RECEIVERS
        const history = (unref(previewDataHistories) || []).find(h => h.actionType === targetActionType)
        return lodash.get(history, 'subject')
      }),
      displaySubject: computed({
        get () {
          let fallbackSubject = setupData.historySubject
          if (!setupData.hadChangedEmailMetadata) {
            const project = unref(currentTenantProject)
            const tenancy = unref(currentTenantProject).tenancy
            const projectName = getProjectName(project, tenancy)
            fallbackSubject = proxy.$t('tenantProjects.greenFitOut.emailSubject', [projectName])
          }
          return setupData.pendingSubject || fallbackSubject
        },
        set (val) {
          setupData.pendingSubject = val
        },
      }),
      isPICAndNotSent: computed(() => {
        return unref(isPICAndCanManage) && !hasSend.value
      }),

      shouldShowSubject: computed(() => {
        return canProjectRead.value
      }),
      canPreview,
      canSend,
      canCommentOnGreenFitout,
      ccEmailsByRight,
      shouldShowReceivers: computed(() => {
        return canPreview.value && !unref(canEditRecipients)
      }),
    })

    const buildReceiversMetadata = () => {
      const metadataReceivers = processDuplicateReceivers(unref(receivers))
      return {
        receivers: metadataReceivers,
        subject: setupData.displaySubject,
      }
    }

    const save = async (reqPayload = {}, addHistories = []) => {
      const { id, lock } = unref(props.previewData)
      const requestBody = buildUpdatePostFitoutRequest({
        lock,
        ...reqPayload,
      }, {
        addHistory: addHistories,
      })
      await updateGreenFitoutSubmission(id, requestBody)
      utils.resetDirty()
      await refresh()
    }

    const formRef = useRefForm()
    const validateReceivers = formRef.validate
    const saveReceivers = async () => {
      if (!validateReceivers()) return
      const receiversHistory = buildGreenFitOutHistory(
        GREEN_FITOUT_HISTORY_TYPE.SUBMISSION,
        GREEN_FITOUT_HISTORY_ACTION_TYPE.RECEIVERS,
        buildReceiversMetadata(),
      )
      await save({}, [receiversHistory])
    }

    const activeTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    const send = async () => {
      const actionType = !unref(hasSend) ? GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND : GREEN_FITOUT_HISTORY_ACTION_TYPE.RESEND
      const metaDataReceivers = processDuplicateReceivers(unref(receivers))
      const extMetaData = {
        subject: setupData.displaySubject,
        to: metaDataReceivers.to,
        cc: metaDataReceivers.cc,
        bcc: metaDataReceivers.bcc,
        docIds: unref(files).map(file => file.id),
        action: actionType,
        type: unref(activeTabType),
        submission: lodash.cloneDeep(unref(props.previewData)),
      }
      const addHistories = [
        buildGreenFitOutHistory(GREEN_FITOUT_HISTORY_TYPE.SUBMISSION, actionType, buildReceiversMetadata()),
      ]
      // @TODO The status should be COMPLETED, and the completedDate should be set by API
      const reqPayload = unref(hasSend) ? {} : { completedDate: Date.now() }
      await save(reqPayload, addHistories)
      await sendEmail(EmailTypes.tenantProjects.completeGreenFitOut, extMetaData, false)
    }
    const sendToReceivers = async () => {
      if (!validateReceivers()) return
      await send()
      modals.close()
    }

    return {
      ...toRefs(setupData),
      ...formRef,
      ...modals,
      defaultReceivers,
      receivers,
      hasSend,
      files,
      canEditRecipients,
      previewDataHistories,
      latestHistoryReceivers,
      previewDataId,
      previewDataOrder,
      activeBatch,
      saveReceivers,
      sendToReceivers,
      openModalHandle: async () => {
      // init and reset some data
        setupData.pendingSubject = ''
        setupData.pendingReceivers = {}
      },
      afterCloseHandle: () => {
        utils.resetDirty()
      },
    }
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/email_preview';

.nav_btn {
  width: 100%;
  & > * {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
}

</style>
